import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import Select from '../../../../components/forms/Select';
import StatusLabel from '../../../../components/forms/StatusLabel';
import {
  itemPriceEditRoles,
  ItemPriceFilters,
  ItemPriceModel,
  UnitType,
  UnitTypeOptions
} from '../../../../api/pricing/pricing.types';
import { ItemPriceActionTypes } from '../../types/itemprice.types';
import { getItemPriceList, updateItemPriceFilters } from '../../actions/itemprice.actions';
import BooleanLabel from '../../../../components/forms/BooleanLabel';
import { ItemGroupType, ItemModel } from '../../../../api/core/core.types';
import { getItemList } from '../../../setup/actions/item.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { AdminApi } from '../../../../api/api.apiclient';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ClassifierModel, ClassifierTypes, SupplierModel } from '../../../../api/admin/admin.types';
import { formatDate } from '../../../../helpers/date.helper';
import ListViewTable from '../../../../components/tables/ListViewTable';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';

interface ItemPriceProps extends RouteComponentProps, WithTranslation {
  getItemPriceList: (filters?: ItemPriceFilters, page?: PageRequest) => Promise<void>;
  getItemList: () => void;
  itemPrices: ItemPriceModel[] | null;
  items: ItemModel[];
  groupType: ItemGroupType;
  user: UserModel | null;
  supplier: SupplierModel | null;
  totalElements: number;
  totalPages: number;
  filters: ItemPriceFilters | null;
  updateFilters: (filters: ItemPriceFilters) => void;
}

interface ItemPriceState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string | undefined;
  filtersUpdated: boolean;
  filtered: { value: any, id: any }[];
  otaCodes: ClassifierModel[];
  filter: {
    statusFilter: {
      value: { label: string; value: string }[];
    };
  };
}

const ItemPriceSortFields = [
  { value: 'coreItemId', label: 'name' },
  { value: 'validFrom', label: 'validFrom' },
  { value: 'itemOtaCode', label: 'otaCode' }
];

class ItemPrices extends React.Component<ItemPriceProps, ItemPriceState> {
  constructor(props: ItemPriceProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'name',
      filtersUpdated: false,
      filtered: [],
      otaCodes: [],
      filter: {
        statusFilter: {
          value: []
        }
      }
    };
  }

  componentDidMount() {
    this.props.getItemList();
    this.props.getItemPriceList({ itemGroupType: this.props.groupType });

    AdminApi.Classifiers.listAllClassifiersByType(ClassifierTypes.ITEM_OTA_CODE).then((response) => {
      this.setState({ otaCodes: response });
    });
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) {
      this.updateFilterValues();
    }
  }

  updateFilterValues = () => {
    this.setState({ filtersUpdated: true });
    const filters = this.props.filters;
    if (filters) {
      this.setState({
        filtersUpdated: true,
        filter: {
          statusFilter: {
            value: filters.statusType ? [ {
              value: filters.statusType,
              label: filters.statusType
            } ] : []
          }
        }
      });
    }
  };

  getCrumb = () => {
    switch (this.props.groupType) {
      case ItemGroupType.COVERAGE: {
        return 'protections';
      }
      case ItemGroupType.EXTRA: {
        return 'extras';
      }
      case ItemGroupType.FEE: {
        return 'fees';
      }
      default: {
        return 'itemprices';
      }
    }
  };

  getPageTitle = () => {
    const { t } = this.props;

    switch (this.props.groupType) {
      case ItemGroupType.COVERAGE: {
        return t('page_title.protections');
      }
      case ItemGroupType.EXTRA: {
        return t('page_title.extras');
      }
      case ItemGroupType.FEE: {
        return t('page_title.fees');
      }
      default: {
        return 'Itemprices';
      }
    }
  };

  filterItems = (props: ItemPriceModel[]) => props.filter((itemPrice) => itemPrice.itemGroupType === this.props.groupType);

  taxableCell = (props: any) => (<BooleanLabel value={props.getValue().toString()}/>);

  taxIncludedCell = (props: any) => (<BooleanLabel value={props.getValue().toString()}/>);

  statusCell = (props: any) => (<StatusLabel status={props.getValue()}/>);

  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    const filter = this.state.filter;
    this.setState({
      filtered: [
        { value: filter.statusFilter.value, id: 'statusType' }
      ]
    });

  };

  clearFilters = () => {
    this.props.getItemPriceList({ itemGroupType: this.props.groupType });
    this.setState({
      selectedPageNumber: 1,
      filter: {
        statusFilter: {
          value: []
        }
      },
      filtered: []
    });
    this.props.updateFilters({
      statusType: this.state.filter.statusFilter.value[ 0 ]?.value,
      itemGroupType: this.props.groupType
    });
  };

  onRowClick = (rowInfo: any) => this.props.history.push(`/rates/${this.getCrumb()}/${rowInfo.original.id}/view`);


  coreItemIdCell = (props: any) => {
    const item = this.props.items.find((d) => d.id === props.getValue());
    if (item) {
      return <div>{item.name}</div>;
    }

    return <div>{props.getValue()}</div>;
  };

  priceCell = (props: any) => {
    const { t } = this.props;

    const objectUnit = UnitTypeOptions.find((select) => select.value === props.row.original.unitType);
    if (objectUnit && this.props.supplier) {
      let priceUnit = t('v.item_prices.price.per_day',
        { currency: this.props.supplier.baseCurrencyCode });
      if (props.row.original.unitType === UnitType.PER_CENT) priceUnit = t('v.item_prices.price.percent');
      else if (props.row.original.unitType === UnitType.FIXED) priceUnit = t('v.item_prices.price.fixed');

      return `${props.row.original.price} ${priceUnit}`;
    }

    return props.row.original.price;
  };

  otaCodeCell = (props: any) => {
    const otaCodeValue = this.state.otaCodes.find((c) => c.code === props.row.original.itemOtaCode);

    if (otaCodeValue) return otaCodeValue.stringValue;

    return props.row.original.itemOtaCode;
  };

  validFromCell = (props: any) => {

    return (
      <div className="numeric-content">
        {`${formatDate(props.row.original.validFrom)}`}
      </div>
    );
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string) => {
    this.setState({
      ...this.state,
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    });
    console.log(newValue);
    this.props.getItemPriceList({ statusType: newValue.value, itemGroupType: this.props.groupType }, {
      page: 0,
      size: defaultPageSize,
      sortField: 'name',
      sortDirection: 'ASC'
    });
  };


  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.item_prices.name'),
        accessorKey: 'coreItemId',
        cell: this.coreItemIdCell,
        id: 'coreItemId'
      },
      {
        header: t('ct.item_prices.ota_code'),
        accessorKey: 'itemOtaCode',
        cell: this.otaCodeCell,
        id: 'itemOtaCode'
      },
      { header: 'Valid from', accessorKey: 'validFrom', cell: this.validFromCell, id: 'validFrom' },
      { header: t('ct.item_prices.price'), accessorKey: 'price', cell: this.priceCell, id: 'price' },
      {
        id: 'isTaxable', header: t('ct.item_prices.taxable'),
        accessorKey: 'isTaxable', cell: this.taxableCell
      },
      {
        id: 'isTaxIncluded', header: t('ct.item_prices.tax_included'),
        accessorKey: 'isTaxIncluded', cell: this.taxIncludedCell
      },
      { header: t('ct.item_prices.max_price'), accessorKey: 'maxPrice', id: 'maxPrice' },
      {
        header: t('ct.item_prices.status'), accessorKey: 'statusType', cell: this.statusCell, id: 'statusType'
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = ItemPriceSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: sortFieldName.label });
    return (
      this.props.getItemPriceList({
        statusType: this.state.filter.statusFilter.value[ 0 ]?.value,
        itemGroupType: this.props.groupType
      }, {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      const pageRequestFix = {
        page: pageRequest.page,
        sortDirection: pageRequest.sortDirection,
        sortField: ItemPriceSortFields.find((column) => column.label === pageRequest.sortField)?.label
      };
      this.props.getItemPriceList({
          statusType: this.state.filter.statusFilter.value[ 0 ]?.value,
          itemGroupType: this.props.groupType
        }, pageRequestFix
      );

      this.setState({
        selectedPageNumber: pageRequestFix.page + 1,
        sortOrder: pageRequestFix.sortDirection ?? 'ASC',
        sortedBy: ItemPriceSortFields.find((column) => column.label === pageRequest.sortField)?.label
      });
    }
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{this.getPageTitle()}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, itemPriceEditRoles) && (
              <Link
                to={{
                  pathname: `/rates/${this.getCrumb()}/create`
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="container-fluid filter-container form-group form-inline d-flex justify-content-between">
            <div className="ml-1 mr-1 filters-container-margin-top-btm">
              <Select
                className="mr-1"
                placeholder={t('ph.filter.item_prices.by_status')}
                elWidth={200}
                options={[
                  { value: 'ACTIVE', label: 'ACTIVE' },
                  { value: 'INACTIVE', label: 'INACTIVE' }
                ]}
                onChange={(val) => this.dropdownChange(val, 'statusFilter')}
                closeMenuOnSelect={false}
                isMulti={false}
                handleBlurChanged={() => {
                  const filter = this.state.filter;
                  this.setState({
                    filtered: [
                      { value: filter.statusFilter.value, id: 'statusType' }
                    ]
                  });
                }}
                hideSelectedOptions={false}
                label="status"
              />
            </div>
            <div className="pl-1">
              <button type="button" className="btn btn-text-only mr-1" onClick={this.clearFilters}>
                {t('b.clear_filter')}
              </button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}
              </button>
            </div>
          </form>
          {this.props.itemPrices
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.filterItems(this.props.itemPrices)}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={ItemPriceSortFields.find(({ value, label }) => this.state.sortedBy === label)?.value}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  itemPrices: state.rates.itemPrices.itemPrices,
  items: state.setup.items.items,
  user: state.authentication.user,
  supplier: state.authentication.activeSupplier,
  totalPages: state.rates.itemPrices.totalPages,
  totalElements: state.rates.itemPrices.totalElements,
  filters: state.rates.itemPrices.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, ItemPriceActionTypes>) => ({
  getItemPriceList: (filters?: ItemPriceFilters, page?: PageRequest) => dispatch(getItemPriceList(filters, page)),
  getItemList: () => dispatch(getItemList()),
  updateFilters: (filters: ItemPriceFilters) => dispatch(updateItemPriceFilters(filters))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(ItemPrices));
